/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useMemo, useCallback, useRef, useEffect } from "react";
import type { ReactNode } from "react";
import type { UserResponse } from "../services/userService";
import { companyService, type CompanyDto } from "../services/companyService";
import { userService } from "../services/userService";
import { useAuth } from "../hooks/useAuth";

// Local storage keys
const COMPANIES_CACHE_KEY = "companies_cache";
const SELECTED_COMPANY_KEY = "selected_company_id";

interface CompaniesCache {
  companies: CompanyDto[];
  userCompany: CompanyDto | null;
  userProfile: UserResponse | null;
  userId: string;
}

// Helper functions for local storage
const getCachedCompanies = (userId: string): CompaniesCache | null => {
  try {
    const cached = localStorage.getItem(COMPANIES_CACHE_KEY);
    if (!cached) return null;

    const data: CompaniesCache = JSON.parse(cached);

    if (data.userId !== userId) {
      localStorage.removeItem(COMPANIES_CACHE_KEY);
      return null;
    }

    return data;
  } catch (error) {
    console.error("Error reading companies cache:", error);
    localStorage.removeItem(COMPANIES_CACHE_KEY);
    return null;
  }
};

const setCachedCompanies = (
  companies: CompanyDto[],
  userCompany: CompanyDto | null,
  userProfile: UserResponse | null,
  userId: string
): void => {
  try {
    const cache: CompaniesCache = {
      companies,
      userCompany,
      userProfile,
      userId,
    };
    localStorage.setItem(COMPANIES_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.error("Error saving companies cache:", error);
  }
};

const clearCompaniesCache = (): void => {
  try {
    localStorage.removeItem(COMPANIES_CACHE_KEY);
  } catch (error) {
    console.error("Error clearing companies cache:", error);
  }
};

interface CompanyContextType {
  selectedCompanyId: string;
  setSelectedCompanyId: (companyId: string) => void;
  isCompanyLoaded: boolean;
  setIsCompanyLoaded: (loaded: boolean) => void;
  userProfile: UserResponse | null;
  setUserProfile: (profile: UserResponse | null) => void;
  // New fields for company data
  companies: CompanyDto[];
  userCompany: CompanyDto | null;
  isLoading: boolean;
  error: string | null;
  availableCompanies: CompanyDto[];
  refreshCompanies: () => Promise<void>;
  setSelectedCompany: (companyId: string) => void;
  isSuperAdmin: boolean;
  userRoleName: string | null;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const CompanyProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { currentAccount, isAuthenticated, isAuthResolving } = useAuth();

  const [selectedCompanyId, setSelectedCompanyId] = useState<string>(
    () => localStorage.getItem(SELECTED_COMPANY_KEY) || ""
  );
  const [isCompanyLoaded, setIsCompanyLoaded] = useState<boolean>(false);
  const [userProfile, setUserProfile] = useState<UserResponse | null>(null);
  const [companies, setCompanies] = useState<CompanyDto[]>([]);
  const [userCompany, setUserCompany] = useState<CompanyDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const hasLoadedRef = useRef(false);
  const hasSetInitialCompanyRef = useRef(false);
  const selectedCompanyIdRef = useRef<string>(selectedCompanyId || "");

  useEffect(() => {
    selectedCompanyIdRef.current = selectedCompanyId || "";
    // Persist selection so it survives a page refresh instead of resetting to "all".
    if (selectedCompanyId) {
      localStorage.setItem(SELECTED_COMPANY_KEY, selectedCompanyId);
    } else {
      localStorage.removeItem(SELECTED_COMPANY_KEY);
    }
  }, [selectedCompanyId]);

  // Clear company cache on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      clearCompaniesCache();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Fetch companies - this is now centralized in the context
  const fetchCompanies = useCallback(
    async (forceRefresh = false) => {
      if (!isAuthenticated || !currentAccount) return;

      const userId =
        currentAccount.localAccountId || currentAccount.homeAccountId || "";
      
      // Prevent duplicate calls using ref
      if (hasLoadedRef.current && !forceRefresh) {
        return;
      }
      
      if (!forceRefresh) {
        const cached = getCachedCompanies(userId);
        if (cached) {
          setCompanies(cached.companies);
          setUserCompany(cached.userCompany);
          setUserProfile(cached.userProfile);
          const currentCompanyId = selectedCompanyIdRef.current;
          if (!hasSetInitialCompanyRef.current && (!currentCompanyId || currentCompanyId === "")) {
            const isSuperAdminFromCache = cached.userProfile?.roleName
              ?.toLowerCase()
              .includes("super");
            
            if (isSuperAdminFromCache) {
              setSelectedCompanyId("all");
            } else {
              if (cached.userProfile?.companyId) {
                setSelectedCompanyId(cached.userProfile.companyId);
              } else {
                setSelectedCompanyId("");
              }
            }
            hasSetInitialCompanyRef.current = true;
          } else if (!hasSetInitialCompanyRef.current) {
            // A selection was restored from a previous session - make sure it's still valid.
            const isSuperAdminFromCache = cached.userProfile?.roleName
              ?.toLowerCase()
              .includes("super");
            if (
              isSuperAdminFromCache &&
              currentCompanyId !== "all" &&
              !cached.companies.some((c) => c.id === currentCompanyId)
            ) {
              setSelectedCompanyId("all");
            } else if (
              !isSuperAdminFromCache &&
              cached.userProfile?.companyId &&
              currentCompanyId !== cached.userProfile.companyId
            ) {
              setSelectedCompanyId(cached.userProfile.companyId);
            }
            hasSetInitialCompanyRef.current = true;
          }

          setIsCompanyLoaded(true);
          hasLoadedRef.current = true;
          return;
        }
      }

      setIsLoading(true);
      setError(null);

      try {
        // Single API call for user profile
        const userProfileResponse = await userService.getCurrentUser();
        setUserProfile(userProfileResponse);

        const isSuperAdminRole = userProfileResponse.roleName
          ?.toLowerCase()
          .includes("super");

        let fetchedCompanies: CompanyDto[] = [];
        let fetchedUserCompany: CompanyDto | null = null;

        if (isSuperAdminRole) {
          const allCompaniesResponse = await companyService.getAll();
          fetchedCompanies = allCompaniesResponse;

          if (
            userProfileResponse.companyId &&
            userProfileResponse.companyId.trim() !== ""
          ) {
            const userCompanyResponse =
              await userService.getCurrentUserCompany();
            fetchedUserCompany = userCompanyResponse;
          }
          const currentCompanyId = selectedCompanyIdRef.current;
          if (!hasSetInitialCompanyRef.current && (!currentCompanyId || currentCompanyId === "")) {
            setSelectedCompanyId("all");
            hasSetInitialCompanyRef.current = true;
          } else if (!hasSetInitialCompanyRef.current) {
            // A selection was restored from a previous session - make sure it's still valid.
            if (
              currentCompanyId !== "all" &&
              !fetchedCompanies.some((c) => c.id === currentCompanyId)
            ) {
              setSelectedCompanyId("all");
            }
            hasSetInitialCompanyRef.current = true;
          }
        } else {
          if (
            userProfileResponse.companyId &&
            userProfileResponse.companyId.trim() !== ""
          ) {
            const userCompanyResponse =
              await userService.getCurrentUserCompany();
            fetchedUserCompany = userCompanyResponse;
            fetchedCompanies = [userCompanyResponse];
            const currentCompanyId = selectedCompanyIdRef.current;
            if (!hasSetInitialCompanyRef.current && (!currentCompanyId || currentCompanyId === "")) {
              setSelectedCompanyId(userProfileResponse.companyId);
              hasSetInitialCompanyRef.current = true;
            } else if (!hasSetInitialCompanyRef.current) {
              // Non-super users only ever have their own company - a restored
              // selection from a different account is invalid here.
              if (currentCompanyId !== userProfileResponse.companyId) {
                setSelectedCompanyId(userProfileResponse.companyId);
              }
              hasSetInitialCompanyRef.current = true;
            }
          } else {
            fetchedCompanies = [];
            setSelectedCompanyId("");
          }
        }

        setCompanies(fetchedCompanies);
        setUserCompany(fetchedUserCompany);

        setCachedCompanies(
          fetchedCompanies,
          fetchedUserCompany,
          userProfileResponse,
          userId
        );
        setIsCompanyLoaded(true);
        hasLoadedRef.current = true;
      } catch (err) {
        console.error("Error fetching companies:", err);
        setError(
          err instanceof Error ? err.message : "Failed to fetch companies"
        );
      } finally {
        setIsLoading(false);
      }
    },
    [isAuthenticated, currentAccount]
  );

  // Load companies on auth change
  useEffect(() => {
    // MSAL reports accounts as empty for a brief moment on every mount/refresh
    // while it re-hydrates the cached session (see useAuth's isAuthResolving).
    // Treating that transient window as "logged out" would wipe the persisted
    // company selection before MSAL ever gets to report the real, authenticated
    // state. Wait it out instead of reacting to a false "not authenticated".
    if (isAuthResolving) {
      return;
    }

    if (isAuthenticated) {
      fetchCompanies(false);
    } else {
      setCompanies([]);
      setUserCompany(null);
      setSelectedCompanyId("");
      setUserProfile(null);
      setIsCompanyLoaded(false);
      clearCompaniesCache();
      localStorage.removeItem(SELECTED_COMPANY_KEY);
      hasLoadedRef.current = false;
      hasSetInitialCompanyRef.current = false;
    }
  }, [isAuthenticated, isAuthResolving, fetchCompanies]);

  const refreshCompanies = useCallback(async () => {
    await fetchCompanies(true);
  }, [fetchCompanies]);

  const setSelectedCompany = useCallback(
    (companyId: string) => {
      const isSuperAdminRole = userProfile?.roleName
        ?.toLowerCase()
        .includes("super");

      if (!isSuperAdminRole) {
        console.warn("User is restricted to their assigned company");
        return;
      }
      setSelectedCompanyId(companyId);
    },
    [userProfile?.roleName]
  );

  const isSuperAdmin = Boolean(
    userProfile?.roleName?.toLowerCase().includes("super")
  );

  const availableCompanies = isSuperAdmin
    ? companies
    : userCompany
    ? [userCompany]
    : [];

  const value = useMemo(
    () => ({
      selectedCompanyId,
      setSelectedCompanyId,
      isCompanyLoaded,
      setIsCompanyLoaded,
      userProfile,
      setUserProfile,
      companies,
      userCompany,
      isLoading,
      error,
      availableCompanies,
      refreshCompanies,
      setSelectedCompany,
      isSuperAdmin,
      userRoleName: userProfile?.roleName || null,
    }),
    [
      selectedCompanyId,
      isCompanyLoaded,
      userProfile,
      companies,
      userCompany,
      isLoading,
      error,
      availableCompanies,
      refreshCompanies,
      setSelectedCompany,
      isSuperAdmin,
    ]
  );

  return (
    <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>
  );
};

export const useCompanyContext = () => {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error("useCompanyContext must be used within a CompanyProvider");
  }
  return context;
};